import { Button, PasswordInput } from "@caprikorn/dynamo-ui";
import type { FC } from "react";
import { Controller, useForm } from "react-hook-form";

import { FORM_VALIDATION_MODE } from "@core/core.constants";
import type { ResetPasswordFormData } from "@hooks/useResetPasswordForm.types";
import { ArrowRightMd } from "@icons";
import type { PutUpdatePasswordParams } from "@services/apiEcms/apiEcms.types";
import { usePutUpdatePassword } from "@services/apiEcms/usePutUpdatePassword";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./ResetPasswordForm.module.css";

export const ResetPasswordForm: FC = () => {
  const { t } = useCustomTranslation(["main", "common"]);
  const { mutate } = usePutUpdatePassword();

  const {
    handleSubmit,
    reset: resetForm,
    control,
    getValues,
  } = useForm<ResetPasswordFormData>({
    mode: FORM_VALIDATION_MODE,
    defaultValues: {
      previousPassword: "",
      proposedPassword: "",
      confirmNewPassword: "",
    },
  });

  const onSubmit = (data: PutUpdatePasswordParams) => {
    mutate(data, {
      onSuccess: () => resetForm(),
    });
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="previousPassword"
        render={({ field, fieldState }) => (
          <PasswordInput
            {...field}
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("settings.resetPassword.currentPasswordLabel")}
            placeholder={t("settings.resetPassword.placeholder")}
          />
        )}
        rules={{ required: "form.error.required" }}
      />
      <Controller
        control={control}
        name="proposedPassword"
        render={({ field, fieldState }) => (
          <PasswordInput
            {...field}
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("settings.resetPassword.newPasswordLabel")}
            placeholder={t("settings.resetPassword.placeholder")}
          />
        )}
        rules={{
          deps: ["confirmNewPassword"],
          required: "form.error.required",
          validate: (value) => {
            if (value.length < 8) {
              return "form.error.password.minCharacter";
            }
            if (!/[a-z]/.test(value)) {
              return "form.error.password.minLowercase";
            }
            if (!/[A-Z]/.test(value)) {
              return "form.error.password.minUppercase";
            }
            if (!/[0-9]/.test(value)) {
              return "form.error.password.minDigit";
            }
            if (!/[!'#"*`@$%^&(){}[\]:;<>,.?~_+\-=§€£°|/\\]/.test(value)) {
              return "form.error.password.minSpecial";
            }
            return true;
          },
        }}
      />
      <Controller
        control={control}
        name="confirmNewPassword"
        render={({ field, fieldState }) => (
          <PasswordInput
            {...field}
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("settings.resetPassword.confirmPasswordLabel")}
            placeholder={t("settings.resetPassword.placeholder")}
          />
        )}
        rules={{
          deps: ["proposedPassword"],
          required: "form.error.required",
          validate: (value) =>
            value === getValues("proposedPassword") ||
            "form.error.password.equalPassword",
        }}
      />
      <Button
        data-testid="AuthFormTemplate-login-submit"
        endIcon={ArrowRightMd}
        type="submit"
      >
        {t("button.confirm", { ns: "common" })}
      </Button>
    </form>
  );
};
